import {
    AbortedVegnettAction,
    ActionType,
    ErrorAction,
    FetchVegnettAction,
    GetState,
    ReceivedVegnettAction,
} from '@/actions/actiontypes';
import { Data } from '@/bootstrap/data';
import { Vegnettlenke } from '@/domain/vegnett/Vegnettlenke';
import {
    stateMap,
    stateOmrader,
    stateVegnett,
    stateVegnettFilter,
} from '@/selectors/selectors';
import { isRequestAborted } from '@/server/server';
import {
    MapState,
    OmraderState,
    RootState,
    Severity,
    VegkartError,
    VegnettState,
    VegnettStatistics,
} from '@/state';
import { Dispatch } from '@/state/store';
import { getFylkerForQuery, geometryTolerance } from './fetchHelpers';
import { Integration } from './integration';
import { CommonQueries, MapQueries, OmraderQueries, VegnettQueries } from './queries';
import { Kommune } from '@/domain/omrader';

let activeVegnettRequestController: AbortController | null = null;

export function abortActiveVegnettRequestV4(): void {
    activeVegnettRequestController?.abort();
    activeVegnettRequestController = null;
}

type NullableVegnettStateUpdate = Partial<{
    vegnettlenker: Vegnettlenke[] | null;
    statistics: VegnettStatistics[] | null;
}>;

function updateNullableVegnettState(
    state: VegnettState,
    update: NullableVegnettStateUpdate
): VegnettState {
    return Object.assign(
        Object.create(VegnettState.prototype) as VegnettState,
        state,
        update
    );
}

function beginVegnettRequest(): AbortController {
    activeVegnettRequestController?.abort();
    const controller = new AbortController();
    activeVegnettRequestController = controller;
    return controller;
}

function finishVegnettRequest(controller: AbortController): void {
    if (activeVegnettRequestController === controller) {
        activeVegnettRequestController = null;
    }
}

export const fetchVegnettV4 =
    () =>
    async (dispatch: Dispatch, getState: () => RootState, integration: Integration) => {
        // States
        const mapState: MapState = stateMap(getState());
        const omraderState: OmraderState = stateOmrader(getState());
        const vegnettState: VegnettState = stateVegnett(getState());
        const staticData: Data = integration.data;

        // Timestamp
        const timestamp = getState().searchState.searchDropdownState.timestamp;

        // Query classes
        const commonQueries: CommonQueries = new CommonQueries().withGeometritoleranse(
            geometryTolerance(mapState.zoom) ?? null
        );
        const mapQueries: MapQueries = mapState.toQueries();
        const omraderQueries: OmraderQueries = omraderState.toOmradeQueries(
            staticData.kommuner
        );
        const vegnettQueries: VegnettQueries = stateVegnettFilter(getState());

        function shouldCancel() {
            return hasSearchStateChanged(
                mapQueries,
                omraderQueries,
                vegnettQueries,
                getState,
                staticData.kommuner
            );
        }

        // If filter contains empty arrays, don't fetch and reset road net results.
        if (vegnettQueries.hasEmptyArrays()) {
            activeVegnettRequestController?.abort();
            activeVegnettRequestController = null;
            const resetVegnettAction: ReceivedVegnettAction = {
                type: ActionType.VEGNETT_FETCH_RECEIVED,
                receivedVegnettState: vegnettState
                    .withRoadNetLinks([])
                    .withRoadNetStatistics([]),
            };
            dispatch(resetVegnettAction);
            return;
        }

        // Dispatch loading state.
        const fetchVegnettAction: FetchVegnettAction = {
            type: ActionType.VEGNETT_FETCH_INITIATED,
        };
        dispatch(fetchVegnettAction);
        const requestController = beginVegnettRequest();
        const signal = requestController.signal;

        try {
            if (mapState.zoom < 6) {
                if (!mapState.kartutsnitt) {
                    throw new Error('Kan ikke hente vegnettstatistikk uten kartutsnitt');
                }
                const roadnetStatistics = (
                    await integration.server.getVegnettStatisticsGruppert(
                        mapState.kartutsnitt.toQueryString(),
                        ['fylke'],
                        signal
                    )
                )
                    .map(res => {
                        const fylke = getFylkerForQuery(
                            omraderQueries,
                            staticData.fylker
                        )?.find(f => f?.nummer === res?.fylke);
                        return fylke ? new VegnettStatistics(fylke, res.antall) : null;
                    })
                    .filter(res => res != null);

                const receivedVegnettState = updateNullableVegnettState(
                    vegnettState
                        .withRoadNetStatistics(roadnetStatistics)
                        .withFilter(vegnettQueries)
                        .withLoading(false)
                        .withProgress(100),
                    { vegnettlenker: null }
                );
                let action: ReceivedVegnettAction | AbortedVegnettAction = {
                    type: ActionType.VEGNETT_FETCH_RECEIVED,
                    receivedVegnettState,
                };

                if (shouldCancel()) {
                    action = {
                        type: ActionType.VEGNETT_FETCH_ABORTED,
                    };
                }
                dispatch(action);
                return;
            }

            if (mapState.zoom > 13) {
                await updateVegnettWithPaginationLoop(
                    commonQueries,
                    mapQueries,
                    omraderQueries,
                    vegnettQueries,
                    integration,
                    getState,
                    omraderQueries,
                    timestamp,
                    dispatch,
                    undefined,
                    signal
                );
                return;
            }

            const response = await findRoadSysRefAndTotalsFromProbeResults(
                mapQueries,
                omraderQueries,
                integration,
                signal
            );
            const antall = response.total;
            if (antall > integration.config.maximum_number_of_roadnet) {
                const roadNetStatistics = Array.from(response.fylke.entries())
                    .map(([fylkeNummer, antall]) => {
                        const fylke = staticData.fylker.find(
                            f => f.nummer === fylkeNummer
                        );
                        return fylke ? new VegnettStatistics(fylke, antall) : null;
                    })
                    .filter(
                        (statistics): statistics is VegnettStatistics =>
                            statistics !== null
                    );

                const receivedVegnettState = updateNullableVegnettState(
                    vegnettState
                        .withRoadNetStatistics(roadNetStatistics)
                        .withFilter(vegnettQueries)
                        .withLoading(false)
                        .withProgress(100),
                    { vegnettlenker: null }
                );
                let action: ReceivedVegnettAction | AbortedVegnettAction = {
                    type: ActionType.VEGNETT_FETCH_RECEIVED,
                    receivedVegnettState,
                };

                if (shouldCancel()) {
                    action = {
                        type: ActionType.VEGNETT_FETCH_ABORTED,
                    };
                }

                dispatch(action);
                return;
            }

            await updateVegnettWithPaginationLoop(
                commonQueries,
                mapQueries,
                omraderQueries,
                vegnettQueries,
                integration,
                getState,
                omraderQueries.withVegsystemreferanser(
                    response.roadRef ?? omraderQueries.vegsystemreferanse
                ),
                timestamp,
                dispatch,
                antall,
                signal
            );
        } catch (rejection) {
            if (isRequestAborted(rejection) || shouldCancel()) {
                dispatch({ type: ActionType.VEGNETT_FETCH_ABORTED });
                return;
            }

            let rejectionResponse: unknown;
            if (typeof rejection === 'object' && rejection !== null) {
                rejectionResponse = (rejection as { response?: unknown }).response;
            }

            const receivedVegnettAction: ReceivedVegnettAction = {
                type: ActionType.VEGNETT_FETCH_RECEIVED,
                receivedVegnettState: stateVegnett(getState())
                    .withLoading(false)
                    .withProgress(100),
            };
            dispatch(receivedVegnettAction);

            const error = new VegkartError(
                'Feil ved henting av vegnett.',
                { response: rejectionResponse },
                Severity.ERROR
            );
            const errorAction: ErrorAction = { type: ActionType.ERROR_OCCURRED, error };
            dispatch(errorAction);
        } finally {
            finishVegnettRequest(requestController);
        }
    };

const updateVegnettWithPaginationLoop = async (
    genericQueries: CommonQueries,
    mapQueries: MapQueries,
    omraderQueries: OmraderQueries,
    vegnettQueries: VegnettQueries,
    integration: Integration,
    getState: GetState,
    adjustedOmraderQueries: OmraderQueries,
    timestamp: Date | null,
    dispatch: Dispatch,
    totalLinks: number = 10000, // Arbitrary default value for totalLinks
    signal?: AbortSignal
): Promise<void> => {
    const allLinks: Vegnettlenke[] = [];
    let start = genericQueries.start;

    function shouldCancel() {
        return hasSearchStateChanged(
            mapQueries,
            omraderQueries,
            vegnettQueries,
            getState,
            integration.data.kommuner
        );
    }

    let pageNumber = 0;
    while (true) {
        if (shouldCancel()) {
            dispatch({ type: ActionType.VEGNETT_FETCH_ABORTED });
            return;
        }

        const query = start === null ? genericQueries : genericQueries.withStart(start);
        const fragment = await integration.server.getSegmentertVegnett(
            query,
            mapQueries,
            adjustedOmraderQueries,
            vegnettQueries,
            timestamp,
            signal
        );

        if (shouldCancel()) {
            dispatch({ type: ActionType.VEGNETT_FETCH_ABORTED });
            return;
        }

        allLinks.push(...fragment.objekter);

        if (fragment.metadata.returnert < fragment.metadata.sidestorrelse) {
            break;
        }

        start = fragment.metadata.neste.start;

        const totalPages = Math.ceil(totalLinks / fragment.metadata.sidestorrelse);
        const progress = (++pageNumber / totalPages) * 100;

        if (progress >= 100) {
            continue;
        }

        dispatch({
            type: ActionType.VEGNETT_FETCH_PROGRESS,
            progress: progress,
        });
    }

    if (shouldCancel()) {
        dispatch({ type: ActionType.VEGNETT_FETCH_ABORTED });
        return;
    }

    dispatch({
        type: ActionType.VEGNETT_FETCH_RECEIVED,
        receivedVegnettState: updateNullableVegnettState(
            stateVegnett(getState())
                .withRoadNetLinks(allLinks)
                .withFilter(vegnettQueries)
                .withLoading(false)
                .withProgress(100),
            { statistics: null }
        ),
    });
};

interface ProbeResult {
    roadRef: string[] | null;
    fylke: Map<number, number>;
    total: number;
}

async function findRoadSysRefAndTotalsFromProbeResults(
    mapQueries: MapQueries,
    omraderQueries: OmraderQueries,
    integration: Integration,
    signal?: AbortSignal
): Promise<ProbeResult> {
    // If vegsystemreferanse already exists in search, override probing.
    if (omraderQueries.vegsystemreferanse?.length > 0) {
        let antall = 0;
        if (omraderQueries.areasLength() > 0) {
            for (const omradetypeQueries of omraderQueries.split()) {
                await integration.server
                    .getSegmentertVegnett(
                        new CommonQueries(),
                        mapQueries,
                        omradetypeQueries,
                        new VegnettQueries(),
                        new Date(),
                        signal
                    )
                    .then(response => {
                        antall += response.metadata.antall;
                    });
            }
        }
        // return { roadRef: omraderQueries.vegsystemreferanse, total: antall };
        return {
            roadRef: omraderQueries.vegsystemreferanse,
            fylke: new Map<number, number>(),
            total: antall,
        };
    }

    const refResult = new Map<string, number>();
    const countyResult = new Map<number, number>();
    if (!mapQueries.kartutsnitt) {
        throw new Error('Kan ikke hente vegnettstatistikk uten kartutsnitt');
    }

    const res = await integration.server.getVegnettStatisticsGruppert(
        mapQueries.kartutsnitt,
        ['vegkategori', 'fylke'],
        signal
    );

    res.forEach(group => {
        if (
            omraderQueries.fylke.length != 0 &&
            !omraderQueries.fylke.includes(group.fylke.toString())
        )
            return;

        if (group.vegkategori)
            refResult.set(
                group.vegkategori,
                (refResult.get(group.vegkategori) ?? 0) + group.antall
            );

        if (group.fylke)
            countyResult.set(
                group.fylke,
                (countyResult.get(group.fylke) ?? 0) + group.antall
            );
    });

    const limit = integration.config.maximum_number_of_roadnet;
    const refTotal = getTotal(refResult);
    if (refTotal < limit) return { roadRef: null, fylke: countyResult, total: refTotal };

    const roadRefRequestAll = ['E', 'R', 'F', 'K', 'P', 'S'];
    const roadRefRequest = [...roadRefRequestAll];

    while (getTotal(refResult) > limit && refResult.size > 0) {
        refResult.delete(roadRefRequest.pop() ?? '');
    }

    return refResult.size > 0
        ? { roadRef: roadRefRequest, fylke: countyResult, total: getTotal(refResult) }
        : { roadRef: roadRefRequestAll, fylke: countyResult, total: refTotal };
}

function getTotal(map: Map<string, number>): number {
    let total = 0;

    for (const value of map.values()) {
        total += value;
    }

    return total;
}

const hasSearchStateChanged = (
    mapQueries: MapQueries,
    omraderQueries: OmraderQueries,
    vegnettQueries: VegnettQueries,
    getState: GetState,
    kommuner: Kommune[]
): boolean => {
    return (
        JSON.stringify(stateMap(getState()).toQueries()) !== JSON.stringify(mapQueries) ||
        JSON.stringify(stateOmrader(getState()).toOmradeQueries(kommuner)) !==
            JSON.stringify(omraderQueries) ||
        JSON.stringify(stateVegnett(getState()).toQueries()) !==
            JSON.stringify(vegnettQueries)
    );
};
